import {
    UserInvitation,
    UserInviteRequest,
    UserInviteResponse,
    UserOnboardingData
} from '../../types/userInvitation';
import { db } from '../firebase/client';
import {
    collection,
    doc,
    getDocs,
    query,
    where,
    Timestamp,
    serverTimestamp,
    collectionGroup,
    // eslint-disable-next-line no-restricted-imports -- Lookup tables are intentionally root-level for magic link public access
    setDoc as rootSetDoc,
    // eslint-disable-next-line no-restricted-imports -- Lookup tables are intentionally root-level for magic link public access  
    updateDoc as rootUpdateDoc
} from 'firebase/firestore';
import { guardedCreate, guardedUpdate } from '../firebase/writeGuard';
import { FIRESTORE_PATHS } from '../firebase/paths';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { twilioService } from '../sms/twilioService';
import { v4 as uuidv4 } from 'uuid';
import { createUserWithEmailAndPassword, getAuth, updateProfile } from 'firebase/auth';

class UserInvitationService {
    private readonly COLLECTION = 'user_invitations';
    private readonly EXPIRATION_HOURS = 48;

    /**
     * Create and send user invitation
     */
    async createInvitation(
        request: UserInviteRequest,
        invitedBy: { id: string; name: string },
        company: { id: string; name: string }
    ): Promise<UserInviteResponse> {
        try {
            // Validate phone number
            const phoneValidation = twilioService.validatePhoneNumber(request.phoneNumber);
            if (!phoneValidation.valid) {
                return {
                    success: false,
                    message: phoneValidation.error || 'Invalid phone number',
                    error: phoneValidation.error,
                };
            }

            // Check for existing invitation
            const existingInvitation = await this.findActiveInvitationByPhone(
                phoneValidation.formatted!,
                company.id
            );

            if (existingInvitation) {
                return {
                    success: false,
                    message: 'An active invitation already exists for this phone number',
                    error: 'DUPLICATE_INVITATION',
                };
            }

            // Generate token and magic link
            const token = uuidv4();
            const baseUrl = window.location.origin;
            const magicLinkUrl = `${baseUrl}/user/onboard/${token}`;

            const now = new Date();
            const expiresAt = new Date(now.getTime() + this.EXPIRATION_HOURS * 60 * 60 * 1000);

            const invitation: UserInvitation = {
                id: uuidv4(),
                companyId: company.id,
                companyName: company.name,
                invitedBy: invitedBy.id,
                invitedByName: invitedBy.name,
                role: request.role,
                phoneNumber: phoneValidation.formatted!,
                email: request.email,
                name: request.name,
                token,
                magicLinkUrl,
                status: 'sent',
                sentAt: now,
                expiresAt,
                smsAttempts: 0,
                createdAt: now,
                updatedAt: now,
            };

            // Save to Firestore using writeGuard (tenant-scoped)
            await guardedCreate(
                FIRESTORE_PATHS.userInvitations(company.id),
                invitation.id,
                this.toFirestore(invitation),
                { userId: invitedBy.id }
            );

            // Create flat lookup document (Safe - root level for public token lookup)
            // NOTE: Lookup tables are intentionally NOT tenant-scoped for magic link access
            const phoneDigits = phoneValidation.formatted!.replace(/\D/g, '');
            const lookupRef = doc(db, FIRESTORE_PATHS.userInvitationLookups, phoneDigits);
            // eslint-disable-next-line no-restricted-syntax -- Root level lookup table
            await rootSetDoc(lookupRef, {
                invitationId: invitation.id,
                companyId: company.id,
                phoneNumber: phoneValidation.formatted!,
                status: invitation.status,
                createdAt: invitation.createdAt,
                updatedAt: invitation.updatedAt,
            });

            // Send SMS via Cloud Function
            try {
                const functions = getFunctions();
                const sendSMS = httpsCallable(functions, 'sendUserInvitationSMS');

                const smsResult = await sendSMS({
                    phoneNumber: phoneValidation.formatted!,
                    companyName: company.name,
                    invitedByName: invitedBy.name,
                    magicLinkUrl,
                    role: request.role
                });

                const smsData = smsResult.data as { success: boolean; messageSid?: string; status?: string };

                await guardedUpdate(
                    FIRESTORE_PATHS.userInvitations(company.id),
                    invitation.id,
                    {
                        smsAttempts: 1,
                        twilioMessageSid: smsData.messageSid,
                        twilioStatus: smsData.status,
                        status: smsData.success ? 'delivered' : 'sent',
                        deliveredAt: smsData.success ? serverTimestamp() : undefined,
                    }
                );

                return {
                    success: true,
                    message: 'Invitation sent successfully',
                    invitationId: invitation.id,
                };
            } catch (smsError: any) {
                console.error('❌ Failed to send SMS:', smsError);
                await guardedUpdate(
                    FIRESTORE_PATHS.userInvitations(company.id),
                    invitation.id,
                    {
                        smsAttempts: 1,
                        lastSMSError: smsError.message || 'SMS sending failed',
                    }
                );

                return {
                    success: false,
                    message: 'Invitation created but SMS failed to send',
                    error: smsError.message,
                    invitationId: invitation.id,
                };
            }
        } catch (error) {
            console.error('❌ Error creating user invitation:', error);
            return {
                success: false,
                message: 'Failed to create invitation',
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    /**
     * Complete onboarding
     * Creates Firebase Auth user and Firestore user record
     */
    async completeOnboarding(
        token: string,
        data: UserOnboardingData
    ): Promise<{ success: boolean; message: string; userId?: string }> {
        try {
            const invitation = await this.getInvitationByToken(token);

            if (!invitation) return { success: false, message: 'Invalid token' };
            if (invitation.status === 'completed') return { success: false, message: 'Already completed' };
            if (new Date() > invitation.expiresAt) return { success: false, message: 'Expired' };

            // 1. Create Auth User
            const auth = getAuth();
            const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password!);
            const user = userCredential.user;

            await updateProfile(user, { displayName: data.name });

            // 2. Create User Record in Firestore using writeGuard
            // Path: companies/{companyId}/users/{userId}
            // CRITICAL: companyId is the only company identifier
            await guardedCreate(
                FIRESTORE_PATHS.users(invitation.companyId),
                user.uid,
                {
                    uid: user.uid,
                    email: data.email,
                    displayName: data.name,
                    firstName: data.name.split(' ')[0],
                    lastName: data.name.split(' ').slice(1).join(' '),
                    phoneNumber: data.phone,
                    role: invitation.role, // 'manager' or 'pumper'
                    // Note: companyId is auto-added by guardedCreate
                    active: true,
                    address: data.address,
                    onboardedAt: serverTimestamp(),
                    invitationId: invitation.id,
                    preferences: {
                        theme: 'system',
                        notifications: true
                    }
                },
                { userId: invitation.invitedBy }
            );

            // 3. Mark invitation as completed using writeGuard
            await guardedUpdate(
                FIRESTORE_PATHS.userInvitations(invitation.companyId),
                invitation.id,
                {
                status: 'completed',
                completedAt: serverTimestamp(),
                createdUserId: user.uid,
                updatedAt: serverTimestamp(),
            });

            // 4. Update Lookup to avoid re-use (root level - not tenant scoped)
            const digits = invitation.phoneNumber.replace(/\D/g, '');
            // eslint-disable-next-line no-restricted-syntax -- Root level lookup table
            await rootUpdateDoc(doc(db, FIRESTORE_PATHS.userInvitationLookups, digits), {
                status: 'completed',
                updatedAt: serverTimestamp()
            });

            return { success: true, message: 'Account created successfully', userId: user.uid };

        } catch (error: any) {
            console.error('❌ Error completing user onboarding:', error);
            return {
                success: false,
                message: error.message || 'Failed to complete onboarding'
            };
        }
    }

    async getInvitationByToken(token: string): Promise<UserInvitation | null> {
        try {
            const q = query(
                collectionGroup(db, this.COLLECTION), // Requires index
                where('token', '==', token)
            );

            const snapshot = await getDocs(q);
            if (snapshot.empty) return null;

            return this.fromFirestore(snapshot.docs[0].data());
        } catch (error) {
            console.error('Error finding invitation by token:', error);
            return null;
        }
    }

    private async findActiveInvitationByPhone(phone: string, companyId: string) {
        // Similar to vendor impl but scoped to user_invitations
        const q = query(
            collection(db, `companies/${companyId}/${this.COLLECTION}`),
            where('phoneNumber', '==', phone),
            where('status', 'in', ['sent', 'delivered', 'accepted'])
        );
        const snap = await getDocs(q);
        return snap.empty ? null : this.fromFirestore(snap.docs[0].data());
    }

    private toFirestore(data: any): any {
        const converted: any = { ...data };
        Object.keys(converted).forEach(key => {
            if (converted[key] instanceof Date) {
                converted[key] = Timestamp.fromDate(converted[key]);
            }
        });
        return converted;
    }

    private fromFirestore(data: any): any {
        const converted: any = { ...data };
        Object.keys(converted).forEach(key => {
            if (converted[key] instanceof Timestamp) {
                converted[key] = converted[key].toDate();
            }
        });
        return converted;
    }
}

export const userInvitationService = new UserInvitationService();
